/**
 * A labelled single-line input with the app's field styling and the required
 * marker the forms in this codebase all draw by hand (BON-472).
 *
 * Shared rather than local to the reservation edit modal: the label/marker/input
 * trio is repeated in every form on the staff app, and each copy is a chance for
 * the label to lose its `htmlFor` — which is the difference between a screen
 * reader announcing "Email, required" and announcing "edit text, blank".
 *
 * `describedBy` and `invalid` (BON-630) are both optional and additive — every
 * existing call site renders exactly as before. `describedBy` lets a caller
 * point the field at its own warning or error text instead of (or alongside)
 * the dialog-level `aria-describedby`, so a screen reader announces the field's
 * OWN description when focus lands on it, not just the dialog's once on open.
 * `invalid` sets `aria-invalid` and a red border in both themes — the visual
 * half of the same "this field has a problem" signal.
 */
export function LabeledTextInput({
  id,
  label,
  value,
  onChange,
  type = 'text',
  required = false,
  maxLength,
  inputRef,
  autoComplete = 'off',
  describedBy,
  invalid = false,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: 'text' | 'email';
  required?: boolean;
  maxLength?: number;
  inputRef?: React.RefObject<HTMLInputElement>;
  autoComplete?: string;
  /** `aria-describedby` for the input itself — e.g. a warning or an inline
   *  error id. */
  describedBy?: string;
  /** Sets `aria-invalid` and a red border, in both themes. */
  invalid?: boolean;
}) {
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-semibold text-bark-900 dark:text-stone-100">
        {label} {required && <span className="text-red-600">*</span>}
      </label>
      <input
        id={id}
        ref={inputRef}
        type={type}
        value={value}
        required={required}
        maxLength={maxLength}
        autoComplete={autoComplete}
        aria-describedby={describedBy}
        aria-invalid={invalid || undefined}
        onChange={(e) => onChange(e.target.value)}
        className={`mt-1.5 w-full rounded-lg border px-3 py-2 text-sm dark:bg-[#1e2520] dark:text-stone-100 ${
          invalid ? 'border-red-500 dark:border-red-500' : 'border-stone-300 dark:border-stone-600'
        }`}
      />
    </div>
  );
}
